import type { DiagnosisSession, IssueId } from './types';

const STORAGE_PREFIX = 'buyer-help:diagnosis:';

function storageKey(issueId: IssueId): string {
  return `${STORAGE_PREFIX}${issueId}`;
}

export function saveSession(session: DiagnosisSession): void {
  try {
    sessionStorage.setItem(storageKey(session.issueId), JSON.stringify(session));
  } catch {
    // Storage is a convenience only. Diagnosis must continue when it is blocked.
  }
}

export function loadSession(issueId: IssueId): DiagnosisSession | undefined {
  try {
    const raw = sessionStorage.getItem(storageKey(issueId));
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!isDiagnosisSession(parsed) || parsed.issueId !== issueId) {
      clearSession(issueId);
      return undefined;
    }
    return parsed;
  } catch {
    clearSession(issueId);
    return undefined;
  }
}

export function clearSession(issueId: IssueId): void {
  try {
    sessionStorage.removeItem(storageKey(issueId));
  } catch {
    // Ignore unavailable storage.
  }
}

function isDiagnosisSession(value: unknown): value is DiagnosisSession {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<DiagnosisSession>;
  return (
    candidate.version === 1 &&
    typeof candidate.issueId === 'string' &&
    Array.isArray(candidate.answers) &&
    (candidate.currentNodeId === undefined || typeof candidate.currentNodeId === 'string') &&
    (candidate.outcomeId === undefined || typeof candidate.outcomeId === 'string')
  );
}
